import { inject, Injectable } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import { IInvoice } from '@tsin-core/models/invoice.model';
import { InvoiceService } from '@tsin-core/services/http/invoice.service';
import {
  GetInvoices,
  GetInvoiceByApplicationId,
  GetInvoicesByExamType,
  GetInvoiceById,
  GetInvoicesByUserId,
  CreateInvoice,
} from './invoices.actions';
import { catchError, tap } from 'rxjs';
import { LoadingState } from '@tsin-core/entities';

export interface InvoiceStateModel {
  invoices: IInvoice[];
  singleInvoice: IInvoice | null;
  loading: LoadingState;
}

@State<InvoiceStateModel>({
  name: 'invoiceState',
  defaults: {
    invoices: [],
    singleInvoice: null,
    loading: LoadingState.notLoading,
  },
})
@Injectable()
export class InvoiceState {
  invoiceService: InvoiceService = inject(InvoiceService);

  @Selector()
  static listInvoices(state: InvoiceStateModel): IInvoice[] {
    return state?.invoices ?? [];
  }

  @Selector()
  static getSingleInvoice(state: InvoiceStateModel): IInvoice | null {
    return state?.singleInvoice ?? null;
  }

  @Selector()
  static getLoading(state: InvoiceStateModel): LoadingState {
    return state.loading;
  }

  @Action(GetInvoices)
  getInvoices(ctx: StateContext<InvoiceStateModel>, action: GetInvoices) {
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.invoiceService.getInvoices(action.params).pipe(
      tap((invoices: IInvoice[]) => {
        ctx.patchState({ invoices, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetInvoiceByApplicationId)
  getInvoiceByApplicationId(
    ctx: StateContext<InvoiceStateModel>,
    action: GetInvoiceByApplicationId
  ) {
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.invoiceService.getInvoiceByApplicationId(action.applicationId).pipe(
      tap((invoice: IInvoice) => {
        ctx.patchState({ singleInvoice: invoice, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetInvoicesByExamType)
  getInvoicesByExamType(
    ctx: StateContext<InvoiceStateModel>,
    action: GetInvoicesByExamType
  ) {
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.invoiceService.getPaymentByExamType(action.examType).pipe(
      tap((invoices: IInvoice[]) => {
        ctx.patchState({ invoices, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }



  @Action(GetInvoiceById)
  getInvoiceById(ctx: StateContext<InvoiceStateModel>, action: GetInvoiceById) {
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.invoiceService.getInvoiceById(action.invoiceId).pipe(
      tap((invoice: IInvoice) => {
        ctx.patchState({ singleInvoice: invoice, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetInvoicesByUserId)
  getInvoicesByUserId(
    ctx: StateContext<InvoiceStateModel>,
    action: GetInvoicesByUserId
  ) {
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.invoiceService.getInvoicesByUserId(action.userId).pipe(
      tap((invoices: IInvoice[]) => {
        ctx.patchState({ invoices, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(CreateInvoice)
  createInvoice(ctx: StateContext<InvoiceStateModel>, action: CreateInvoice) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.invoiceService.createInvoice(action.payload).pipe(
      tap((newInvoice: IInvoice) => {
        const state = ctx.getState();
        ctx.patchState({
          invoices: [...state.invoices, newInvoice],
          singleInvoice: newInvoice,
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }
}
